/**
 * Community conversation — /community/messages/:id
 *
 * Single conversation view. Polls messages every 5s when foreground,
 * 30s when backgrounded, per the C3 design §10. Polls use ``?since=
 * <last>`` so payloads stay small (one-message-on-average per poll).
 */
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  blockMember,
  fetchMessages,
  fetchMyProfile,
  reportConversation,
  sendMessage,
  type DmReportReason,
  type MessageReadDto,
} from "@/lib/community-api";
import { MarkdownComposer } from "@/components/MarkdownComposer";

const POLL_FOREGROUND_MS = 5_000;
const POLL_BACKGROUND_MS = 30_000;

const REPORT_REASONS: DmReportReason[] = [
  "phi",
  "spam",
  "harassment",
  "off_topic",
  "other",
];

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; messages: MessageReadDto[] }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface CommunityConversationPageProps {
  fetchMessagesFn?: typeof fetchMessages;
  sendMessageFn?: typeof sendMessage;
  reportFn?: typeof reportConversation;
  blockFn?: typeof blockMember;
  fetchProfileFn?: typeof fetchMyProfile;
  onUnauthenticated?: (returnTo: string) => void;
  /** Test seam: disable polling. */
  pollEnabled?: boolean;
  /**
   * Override "who am I". When omitted, the page fetches `/me/profile`
   * on mount to populate it. Tests typically inject this directly to
   * avoid a second mock.
   */
  currentUserId?: string;
}

export function CommunityConversationPage({
  fetchMessagesFn = fetchMessages,
  sendMessageFn = sendMessage,
  reportFn = reportConversation,
  blockFn = blockMember,
  fetchProfileFn = fetchMyProfile,
  onUnauthenticated = redirectToLogin,
  pollEnabled = true,
  currentUserId: currentUserIdProp,
}: CommunityConversationPageProps = {}): React.JSX.Element {
  const params = useParams<{ id: string }>();
  const conversationId = params.id ?? "";

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    currentUserIdProp ?? null,
  );
  const [composerBody, setComposerBody] = useState("");
  const [composerError, setComposerError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const lastSeenAtRef = useRef<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<DmReportReason>("spam");
  const [reportNote, setReportNote] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportSubmittedFlagId, setReportSubmittedFlagId] = useState<
    string | null
  >(null);
  const [blockError, setBlockError] = useState<string | null>(null);
  const [blockedUserId, setBlockedUserId] = useState<string | null>(null);
  const [visible, setVisible] = useState(
    typeof document !== "undefined" ? !document.hidden : true,
  );

  useEffect(() => {
    const onVisibility = (): void => {
      setVisible(!document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Identify "who am I" so we can align bubbles + show Block on the
  // other participant. Skip the fetch when the prop is injected (tests).
  useEffect(() => {
    if (currentUserIdProp) return;
    let cancelled = false;
    void (async () => {
      try {
        const profile = await fetchProfileFn();
        if (cancelled) return;
        setCurrentUserId(profile.user_id);
      } catch {
        // If /me/profile fails for any reason (transient network blip,
        // a 403 mid-session), the page still renders — bubbles just
        // can't be aligned and the Block button won't show. The
        // messages still come through and the user can still send.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserIdProp, fetchProfileFn]);

  const loadInitial = useCallback(async (): Promise<void> => {
    try {
      const resp = await fetchMessagesFn(conversationId);
      setState({ kind: "ready", messages: resp.messages });
      const last = resp.messages[resp.messages.length - 1];
      lastSeenAtRef.current = last ? last.created_at : null;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          onUnauthenticated(`/community/messages/${conversationId}`);
          return;
        }
        if (err.status === 404) {
          setState({ kind: "not_found" });
          return;
        }
      }
      const msg =
        err instanceof Error ? err.message : "Could not load this conversation.";
      setState({ kind: "error", message: msg });
    }
  }, [fetchMessagesFn, conversationId, onUnauthenticated]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (state.kind !== "ready" || !pollEnabled) return;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      try {
        const since = lastSeenAtRef.current ?? undefined;
        const resp = await fetchMessagesFn(
          conversationId,
          since ? { since } : undefined,
        );
        if (cancelled || resp.messages.length === 0) return;
        setState((s) =>
          s.kind === "ready"
            ? { kind: "ready", messages: [...s.messages, ...resp.messages] }
            : s,
        );
        const last = resp.messages[resp.messages.length - 1];
        if (last) {
          lastSeenAtRef.current = last.created_at;
        }
      } catch {
        // Polling errors are silently swallowed; the next poll reconciles.
      }
    };

    const interval = visible ? POLL_FOREGROUND_MS : POLL_BACKGROUND_MS;
    const id = window.setInterval(() => {
      void poll();
    }, interval);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchMessagesFn, conversationId, pollEnabled, state.kind, visible]);

  const handleSend = async (): Promise<void> => {
    setSending(true);
    setComposerError(null);
    try {
      const sent = await sendMessageFn(conversationId, composerBody);
      setState((s) =>
        s.kind === "ready"
          ? { kind: "ready", messages: [...s.messages, sent] }
          : s,
      );
      lastSeenAtRef.current = sent.created_at;
      setComposerBody("");
    } catch (err) {
      if (err instanceof ApiError && err.body) {
        const body = err.body as {
          detail?: { blocking?: string; message?: string };
        };
        setComposerError(
          body.detail?.message ?? body.detail?.blocking ?? err.message,
        );
      } else if (err instanceof Error) {
        setComposerError(err.message);
      } else {
        setComposerError("Could not send the message.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleReportSubmit = async (): Promise<void> => {
    setReportSubmitting(true);
    setReportError(null);
    try {
      const resp = await reportFn(
        conversationId,
        reportReason,
        reportNote || undefined,
      );
      setReportSubmittedFlagId(resp.flag_id);
      setReportOpen(false);
    } catch (err) {
      if (err instanceof Error) {
        setReportError(err.message);
      } else {
        setReportError("Could not submit the report.");
      }
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleBlock = async (otherUserId: string): Promise<void> => {
    setBlockError(null);
    try {
      await blockFn(otherUserId);
      setBlockedUserId(otherUserId);
    } catch (err) {
      if (err instanceof Error) {
        setBlockError(err.message);
      } else {
        setBlockError("Could not block this member.");
      }
    }
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading conversation…
        </p>
      </section>
    );
  }

  if (state.kind === "not_found") {
    return (
      <SimpleErrorPage
        title="Conversation not found"
        message="That conversation doesn't exist or you aren't a participant."
      />
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  // Best-effort "other participant" inference from the first message that
  // isn't from the current user. currentUserId is populated either by the
  // test-seam prop or by the /me/profile fetch on mount. If the profile
  // fetch fails OR no incoming message has arrived yet, we punt on the
  // block-button UI; report still works.
  const otherUserIdGuess = currentUserId
    ? state.messages.find((m) => m.sender_user_id !== currentUserId)
        ?.sender_user_id ?? null
    : null;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community/messages" className="hover:text-foreground">
          Messages
        </Link>{" "}
        <span aria-hidden="true">›</span> Conversation
      </nav>

      <header className="mt-3 mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-foreground">Conversation</h1>
        <div className="flex gap-2">
          {otherUserIdGuess && blockedUserId !== otherUserIdGuess && (
            <button
              type="button"
              onClick={() => {
                void handleBlock(otherUserIdGuess);
              }}
              className="rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
            >
              Block
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setReportOpen((o) => !o);
            }}
            className="rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
          >
            Report
          </button>
        </div>
      </header>

      {blockError && (
        <p className="mb-2 text-sm text-red-700" role="alert">
          {blockError}
        </p>
      )}
      {blockedUserId !== null && (
        <p className="mb-2 text-sm text-green-700" role="status">
          Member blocked. They won't be able to start new conversations with you.
        </p>
      )}

      {reportOpen && (
        <form
          className="mb-6 space-y-3 rounded-lg border border-border bg-muted/30 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleReportSubmit();
          }}
        >
          <p className="text-sm font-medium text-foreground">
            Report this conversation
          </p>
          <div>
            <label
              htmlFor="report-reason"
              className="block text-xs font-medium"
            >
              Reason
            </label>
            <select
              id="report-reason"
              value={reportReason}
              onChange={(e) => {
                setReportReason(e.target.value as DmReportReason);
              }}
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
            >
              {REPORT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r === "phi"
                    ? "PHI exposure"
                    : r === "off_topic"
                      ? "Off-topic"
                      : r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="report-note" className="block text-xs font-medium">
              Note (optional)
            </label>
            <textarea
              id="report-note"
              value={reportNote}
              onChange={(e) => {
                setReportNote(e.target.value);
              }}
              rows={3}
              maxLength={2_000}
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
            />
          </div>
          {reportError && (
            <p className="text-sm text-red-700" role="alert">
              {reportError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setReportOpen(false);
              }}
              className="rounded-md border border-border px-3 py-1 text-xs"
              disabled={reportSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
              disabled={reportSubmitting}
            >
              {reportSubmitting ? "Submitting…" : "Submit report"}
            </button>
          </div>
        </form>
      )}
      {reportSubmittedFlagId !== null && (
        <p className="mb-4 text-sm text-green-700" role="status">
          Report submitted. An admin will review.
        </p>
      )}

      <ol className="space-y-3">
        {state.messages.length === 0 ? (
          <li className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No messages yet. Say hi.
          </li>
        ) : (
          state.messages.map((m) => (
            <li key={m.id}>
              <MessageBubble message={m} currentUserId={currentUserId ?? ""} />
            </li>
          ))
        )}
      </ol>

      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
      >
        <MarkdownComposer
          label="Message"
          value={composerBody}
          onChange={setComposerBody}
          minLength={1}
          maxLength={4_000}
          placeholder="Send a message…"
          disabled={sending}
          rows={3}
        />
        {composerError && (
          <p className="text-sm text-red-700" role="alert">
            {composerError}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sending || composerBody.length < 1}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </section>
  );
}

function MessageBubble({
  message,
  currentUserId,
}: {
  message: MessageReadDto;
  currentUserId: string;
}): React.JSX.Element {
  const isMine = message.sender_user_id === currentUserId;
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-lg px-4 py-2 text-sm ${
          isMine
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        }`}
      >
        <p className="whitespace-pre-line">
          {message.status === "deleted" ? (
            <span className="italic text-muted-foreground">
              [message removed by sender]
            </span>
          ) : (
            message.body
          )}
        </p>
        <time
          dateTime={message.created_at}
          className={`mt-1 block text-[10px] ${
            isMine ? "text-primary-foreground/80" : "text-muted-foreground"
          }`}
        >
          {new Date(message.created_at).toLocaleString()}
        </time>
      </div>
    </div>
  );
}

function SimpleErrorPage({
  title,
  message,
}: {
  title: string;
  message: string;
}): React.JSX.Element {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-4 text-muted-foreground">{message}</p>
      <Link
        to="/community/messages"
        className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Back to messages
      </Link>
    </section>
  );
}
